/**
 * Sprint 4 (S4.C) — ManualApplicationDialog.
 *
 * Recruiter-facing form to add a candidate's CV manually (e.g. received via
 * email / referral / WhatsApp). Posts a multipart/form-data payload to
 * `POST /companies/:cid/job-positions/:pid/applications` and runs through
 * the same downstream pipeline as the public form: cap check, dedup, AI
 * screening, candidate confirmation email.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2, UploadCloud, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { APPLICATION_SOURCES, channelToBackend } from '@/lib/channels';
import {
  buildSocialProfileSubmissions,
  initialSocialProfileValues,
  resolveSocialProfilesFromPosition,
  socialProfileFieldKey,
  socialProfileLabel,
  socialProfilePlaceholder,
  validateSocialProfileUrl,
} from '@/lib/socialProfiles';
import {
  documentFieldLabel,
  multipartFieldNameForDocumentType,
  resolveDocumentFieldsFromPosition,
} from '@/lib/documentFields';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import type { Channel, JobPosition, JobPosting } from '@/types';
import { toast } from 'sonner';

interface ManualApplicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  /** Pre-fill the position when the dialog is opened from a specific position context. */
  defaultPositionId?: string;
  positions: JobPosition[];
  /** Active postings for the selected position — when omitted, fetched on demand. */
  postings?: JobPosting[];
  /** Hide the position dropdown (e.g. opened from a position detail page). */
  lockPosition?: boolean;
  onSubmitted?: (result: { applicationId: string; positionId: string }) => void;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;

function RequiredLabel({ children }: { children: ReactNode }) {
  return (
    <Label className="text-xs font-medium">
      {children} <span className="text-destructive">*</span>
    </Label>
  );
}

export function ManualApplicationDialog({
  open,
  onOpenChange,
  companyId,
  defaultPositionId,
  positions,
  postings: postingsProp,
  lockPosition = false,
  onSubmitted,
}: ManualApplicationDialogProps): JSX.Element {
  const { t } = useLanguage();

  const eligiblePositions = useMemo(
    () => positions.filter((p) => p.status === 'active'),
    [positions],
  );

  const [positionId, setPositionId] = useState<string>(defaultPositionId ?? '');
  const [source, setSource] = useState<Channel>('Internal');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [socialValues, setSocialValues] = useState<Record<string, string>>({});
  const [documentFiles, setDocumentFiles] = useState<Record<string, File | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fetchedPostings, setFetchedPostings] = useState<JobPosting[]>([]);

  const positionPostings = useMemo(() => {
    const list = postingsProp?.length ? postingsProp : fetchedPostings;
    return list.filter((p) => p.positionId === positionId && p.isActive);
  }, [postingsProp, fetchedPostings, positionId]);

  const hasActivePosting = positionPostings.length > 0;
  const selectedPosition = useMemo(
    () => positions.find((p) => p.id === positionId) ?? null,
    [positions, positionId],
  );
  const configuredSocialProfiles = useMemo(
    () => resolveSocialProfilesFromPosition(selectedPosition ?? {}),
    [selectedPosition],
  );
  const configuredDocumentFields = useMemo(
    () => resolveDocumentFieldsFromPosition(selectedPosition ?? {}),
    [selectedPosition],
  );

  useEffect(() => {
    setSocialValues(initialSocialProfileValues(configuredSocialProfiles));
    setDocumentFiles(Object.fromEntries(
      configuredDocumentFields.map((field) => [multipartFieldNameForDocumentType(field.type), null]),
    ));
  }, [configuredSocialProfiles, configuredDocumentFields, positionId]);

  useEffect(() => {
    if (!open || !companyId || !positionId || postingsProp?.length) return;
    void api.postings.listByPosition(companyId, positionId).then(setFetchedPostings);
  }, [open, companyId, positionId, postingsProp]);

  useEffect(() => {
    if (!open) {
      const id = setTimeout(() => {
        setPositionId(defaultPositionId ?? '');
        setSource('Internal');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setSocialValues({});
        setDocumentFiles({});
        setServerError(null);
        setFetchedPostings([]);
      }, 200);
      return () => clearTimeout(id);
    }
    setPositionId(defaultPositionId ?? '');
    return undefined;
  }, [open, defaultPositionId]);

  const validate = (): string | null => {
    if (!positionId) return t('manualApply.errors.positionRequired');
    if (!source) return t('manualApply.errors.sourceRequired');
    if (!firstName.trim()) return t('manualApply.errors.firstNameRequired');
    if (!lastName.trim()) return t('manualApply.errors.lastNameRequired');
    if (!email.trim()) return t('manualApply.errors.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return t('manualApply.errors.emailInvalid');
    if (!hasActivePosting) return t('manualApply.errors.noActivePosting');
    for (const field of configuredDocumentFields) {
      const multipartName = multipartFieldNameForDocumentType(field.type);
      const file = documentFiles[multipartName];
      const label = documentFieldLabel(field, t);
      if (field.required && !file) {
        return t('apply.documentRequired', { label });
      }
      if (file && file.size > MAX_FILE_BYTES) {
        return t('apply.documentInvalid', { label });
      }
    }
    for (const field of configuredSocialProfiles) {
      const key = socialProfileFieldKey(field);
      const value = socialValues[key]?.trim() ?? '';
      if (field.required && !value) {
        return t('apply.socialRequired', { label: socialProfileLabel(field, t) });
      }
      if (value && !validateSocialProfileUrl(field.type, value)) {
        return t('apply.socialInvalid', { label: socialProfileLabel(field, t) });
      }
    }
    return null;
  };

  const handleSubmit = async (): Promise<void> => {
    setServerError(null);
    const v = validate();
    if (v) {
      setServerError(v);
      return;
    }

    setSubmitting(true);
    try {
      const submissions = buildSocialProfileSubmissions(configuredSocialProfiles, socialValues);
      const linkedIn = submissions.find((s) => s.type === 'linkedin')?.url;
      const documents: Record<string, File> = {};
      for (const field of configuredDocumentFields) {
        const multipartName = multipartFieldNameForDocumentType(field.type);
        const file = documentFiles[multipartName];
        if (file) documents[multipartName] = file;
      }
      const result = await api.applications.createManual(companyId, positionId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        documents,
        source: channelToBackend(source),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(linkedIn ? { linkedinUrl: linkedIn } : {}),
        ...(submissions.length > 0 ? { socialProfiles: submissions } : {}),
      });
      toast.success(t('manualApply.success'));
      onOpenChange(false);
      onSubmitted?.({ applicationId: result.applicationId, positionId });
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.status === 402) {
        onOpenChange(false);
        return;
      }
      setServerError(apiErr?.message ?? (err instanceof Error ? err.message : 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{t('manualApply.title')}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1">
          {t('manualApply.subtitle')}
        </p>

        <div className="space-y-3">
          {!lockPosition && (
            <div className="space-y-1.5">
              <RequiredLabel>{t('manualApply.position')}</RequiredLabel>
              <Select value={positionId} onValueChange={setPositionId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t('manualApply.positionPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {eligiblePositions.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      {positions.length === 0
                        ? t('manualApply.noPositionsInCompany')
                        : t('manualApply.noActivePositions')}
                    </SelectItem>
                  ) : (
                    eligiblePositions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <RequiredLabel>{t('manualApply.source')}</RequiredLabel>
            <Select value={source} onValueChange={(v) => setSource(v as Channel)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t('manualApply.sourcePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {APPLICATION_SOURCES.map((ch) => (
                  <SelectItem key={ch} value={ch}>
                    {ch}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <RequiredLabel>{t('manualApply.firstName')}</RequiredLabel>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1.5">
              <RequiredLabel>{t('manualApply.lastName')}</RequiredLabel>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-9" />
            </div>
          </div>

          <div className="space-y-1.5">
            <RequiredLabel>{t('manualApply.email')}</RequiredLabel>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-9" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">{t('manualApply.phone')}</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9" />
          </div>

          {configuredSocialProfiles.map((field) => {
            const key = socialProfileFieldKey(field);
            return (
              <div key={key} className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {socialProfileLabel(field, t)}
                  {field.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                <Input
                  type="url"
                  placeholder={socialProfilePlaceholder(field.type)}
                  value={socialValues[key] ?? ''}
                  onChange={(e) => setSocialValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="h-9"
                />
              </div>
            );
          })}

          {configuredDocumentFields.map((field) => {
            const multipartName = multipartFieldNameForDocumentType(field.type);
            const file = documentFiles[multipartName];
            const label = documentFieldLabel(field, t);
            return (
              <div key={multipartName} className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {label}
                  {field.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                <div className="rounded-md border border-dashed border-input bg-muted/30 px-3 py-3 flex items-center gap-2">
                  {file ? (
                    <>
                      <UploadCloud className="h-4 w-4 text-primary shrink-0" />
                      <span className="flex-1 truncate text-xs">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => setDocumentFiles((prev) => ({ ...prev, [multipartName]: null }))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer w-full">
                      <UploadCloud className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground flex-1">{t('manualApply.cvHint')}</span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const next = e.target.files?.[0] ?? null;
                          setDocumentFiles((prev) => ({ ...prev, [multipartName]: next }));
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          })}

          <p className="text-[11px] leading-relaxed text-muted-foreground bg-muted/40 border border-border/60 rounded px-2.5 py-2">
            {t('manualApply.aiNotice')}
          </p>

          {serverError && (
            <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-2.5 py-2">
              {serverError}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={() => { void handleSubmit(); }} disabled={submitting}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {t('manualApply.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ManualApplicationDialog;
